import { computed, ref, toRaw, watch } from 'vue'
import { defineStore } from 'pinia'
import {
  builtinSchemes,
  createEmptyDocument,
  createSection,
  createTag,
  duplicateDocument,
  normalizeSectionOrder,
  renderPrompt,
  type MjParams,
  type PlatformId,
  type PromptDocument,
  type Section,
  type Tag,
} from '@prompt-optimize/core'
import { promptRepository } from '@prompt-optimize/storage'
import { createLogger } from '@prompt-optimize/shared'
import { useDebounceFn } from '@vueuse/core'

const log = createLogger('document-store')

export const useDocumentStore = defineStore('document', () => {
  const doc = ref<PromptDocument>(createEmptyDocument('未命名方案'))
  const platform = ref<PlatformId>('midjourney')
  const hydrated = ref(false)
  const schemeList = ref<PromptDocument[]>([])

  const rendered = computed(() => renderPrompt(doc.value, platform.value))

  const starredSchemes = computed(() => schemeList.value.filter(s => s.starred))

  // ---- scheme list ----

  async function refreshList() {
    schemeList.value = await promptRepository.listDocuments().catch((e) => {
      log.error('refreshList', e)
      return []
    })
  }

  // ---- seed builtins ----

  async function seedBuiltins() {
    for (const scheme of builtinSchemes) {
      const existing = await promptRepository.getDocument(scheme.id).catch(() => undefined)
      if (!existing) {
        await promptRepository.saveDocument(JSON.parse(JSON.stringify(scheme))).catch(e =>
          log.error('seed builtin', e),
        )
      }
    }
  }

  // ---- load by id ----

  async function loadById(id: string): Promise<boolean> {
    const found = await promptRepository.getDocument(id).catch((e) => {
      log.error('loadById', e)
      return undefined
    })
    if (!found)
      return false
    doc.value = found
    hydrated.value = true
    return true
  }

  // ---- create new scheme ----

  async function createNew(name = '未命名方案', source?: PromptDocument): Promise<PromptDocument> {
    const next = source
      ? duplicateDocument(source, name)
      : createEmptyDocument(name)
    doc.value = next
    hydrated.value = true
    await promptRepository.saveDocument(JSON.parse(JSON.stringify(toRaw(next)))).catch(e => log.error('createNew persist', e))
    await refreshList()
    return next
  }

  // ---- delete scheme ----

  async function deleteScheme(id: string) {
    await promptRepository.deleteDocument(id).catch(e => log.error('deleteScheme', e))
    await refreshList()
  }

  // ---- duplicate scheme ----

  async function duplicateScheme(id: string): Promise<PromptDocument | undefined> {
    const original = await promptRepository.getDocument(id).catch(() => undefined)
    if (!original)
      return undefined
    const copy = duplicateDocument(original)
    await promptRepository.saveDocument(JSON.parse(JSON.stringify(toRaw(copy)))).catch(e => log.error('duplicateScheme persist', e))
    await refreshList()
    return copy
  }

  // ---- toggle star ----

  async function toggleStar(id?: string) {
    const targetId = id ?? doc.value.id
    try {
      const newStarred = await promptRepository.toggleDocumentStar(targetId)
      // Update local doc if it's the current one
      if (doc.value.id === targetId) {
        doc.value = { ...doc.value, starred: newStarred }
      }
      await refreshList()
      return newStarred
    }
    catch (e) {
      log.error('toggleStar', e)
      return undefined
    }
  }

  // ---- mutations ----

  function replace(next: PromptDocument) {
    doc.value = { ...next, updatedAt: Date.now() }
  }

  function reset() {
    doc.value = createEmptyDocument('未命名方案')
  }

  function addSection(title = '新区块') {
    const order = doc.value.sections.length
    const next = createSection(title, order)
    doc.value = {
      ...doc.value,
      sections: [...doc.value.sections, next],
      updatedAt: Date.now(),
    }
  }

  function removeSection(id: string) {
    doc.value = {
      ...doc.value,
      sections: normalizeSectionOrder(doc.value.sections.filter(s => s.id !== id)),
      updatedAt: Date.now(),
    }
  }

  function updateSectionTitle(id: string, title: string) {
    doc.value = {
      ...doc.value,
      sections: doc.value.sections.map(s => (s.id === id ? { ...s, title } : s)),
      updatedAt: Date.now(),
    }
  }

  function updateSectionTags(id: string, tags: Tag[]) {
    const normalized = tags.map((t) => {
      if (t.id.startsWith('tmp-'))
        return createTag(t.raw, t.weight)
      return t
    })
    doc.value = {
      ...doc.value,
      sections: doc.value.sections.map(s => (s.id === id ? { ...s, tags: normalized } : s)),
      updatedAt: Date.now(),
    }
  }

  function updateSectionEnabled(id: string, enabled: boolean) {
    doc.value = {
      ...doc.value,
      sections: doc.value.sections.map(s => (s.id === id ? { ...s, enabled } : s)),
      updatedAt: Date.now(),
    }
  }

  function reorderSections(next: Section[]) {
    doc.value = {
      ...doc.value,
      sections: next.map((s, i) => ({ ...s, order: i })),
      updatedAt: Date.now(),
    }
  }

  function updateParams(params: MjParams) {
    doc.value = {
      ...doc.value,
      params,
      updatedAt: Date.now(),
    }
  }

  function setPlatform(p: PlatformId) {
    platform.value = p
  }

  // ---- persistence ----

  const persist = useDebounceFn(async () => {
    if (!hydrated.value)
      return
    await promptRepository.saveDocument(JSON.parse(JSON.stringify(toRaw(doc.value)))).catch(e => log.error('persist document', e))
  }, 300)

  watch(doc, () => { void persist() }, { deep: true })

  return {
    // state
    doc,
    platform,
    hydrated,
    schemeList,
    // getters
    rendered,
    starredSchemes,
    // actions
    refreshList,
    seedBuiltins,
    loadById,
    createNew,
    deleteScheme,
    duplicateScheme,
    toggleStar,
    replace,
    reset,
    addSection,
    removeSection,
    updateSectionTitle,
    updateSectionTags,
    updateSectionEnabled,
    reorderSections,
    updateParams,
    setPlatform,
  }
})
